import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import { FaSearch, FaFilePdf } from "react-icons/fa";
import { Link } from "react-router-dom";
import reportService from "../../services/reportService";
import categoryService from "../../services/categoryService";
import subCategoryService from "../../services/subCategoryService";
import { productService } from "../../services/productService";
import InfoPill from "../Common/InfoPill";
import "./Report.css";

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return `₹${number.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const RevenueReport = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalOrderRevenue: 0,
    discountRevenue: 0,
    netProfit: 0,
    totalPurchaseRevenue: 0,
  });

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [filters, setFilters] = useState({
    category_id: "",
    sub_category_id: "",
    product_id: "",
    start_date: "",
    end_date: "",
  });

  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    const loadBaseData = async () => {
      try {
        const [catsRes, prodsRes] = await Promise.all([
          categoryService.getAllCategories({ status: "Active" }),
          productService.getProducts({ limit: 1000 }),
        ]);
        setCategories(catsRes?.categories || catsRes || []);
        setProducts(prodsRes?.products || prodsRes || []);
      } catch (error) {
        console.error("Error loading filter options:", error);
      }
    };

    loadBaseData();
  }, []);

  useEffect(() => {
    if (filters.category_id) {
      loadSubCategoriesByCategory(filters.category_id);
    } else {
      setSubCategories([]);
    }
  }, [filters.category_id]);

  useEffect(() => {
    loadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSubCategoriesByCategory = async (categoryId) => {
    try {
      const response = await subCategoryService.getSubCategoriesByCategoryId(
        categoryId
      );
      const subCats =
        response?.subCategories ||
        response?.data?.subCategories ||
        response?.data ||
        response ||
        [];
      setSubCategories(Array.isArray(subCats) ? subCats : []);
    } catch (error) {
      console.error("Error loading sub categories:", error);
      setSubCategories([]);
    }
  };

  const cleanFilters = (filterValues) => {
    const cleaned = {};
    if (filterValues.category_id)
      cleaned.category_id = filterValues.category_id;
    if (filterValues.sub_category_id)
      cleaned.sub_category_id = filterValues.sub_category_id;
    if (filterValues.product_id) cleaned.product_id = filterValues.product_id;
    if (filterValues.start_date) cleaned.start_date = filterValues.start_date;
    if (filterValues.end_date) cleaned.end_date = filterValues.end_date;
    return cleaned;
  };

  const loadReportData = async (filterParams = null) => {
    try {
      setLoading(true);
      const filtersToUse = filterParams ?? filters;
      const cleanedFilters = cleanFilters(filtersToUse);

      const response = await reportService.getRevenueReport(cleanedFilters);

      // Handle different response structures
      let rows = [];
      let summary = {
        totalOrderRevenue: 0,
        discountRevenue: 0,
        netProfit: 0,
        totalPurchaseRevenue: 0,
      };

      if (response) {
        if (response.rows && Array.isArray(response.rows)) {
          rows = response.rows;
        } else if (
          response.data &&
          response.data.rows &&
          Array.isArray(response.data.rows)
        ) {
          rows = response.data.rows;
        } else if (Array.isArray(response)) {
          rows = response;
        }

        if (response.summary) {
          summary = response.summary;
        } else if (response.data && response.data.summary) {
          summary = response.data.summary;
        }
      }

      setReportData(rows);
      setSummaryMetrics(summary);
    } catch (error) {
      console.error("Error loading revenue report:", error);
      setReportData([]);
      setSummaryMetrics({
        totalOrderRevenue: 0,
        discountRevenue: 0,
        netProfit: 0,
        totalPurchaseRevenue: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const updated = {
        ...prev,
        [field]: value,
        ...(field === "category_id" && { sub_category_id: "", product_id: "" }),
        ...(field === "sub_category_id" && { product_id: "" }),
      };
      filtersRef.current = updated;
      return updated;
    });
  };

  const handleSearch = () => {
    const currentFilters = filtersRef.current;
    loadReportData(currentFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      category_id: "",
      sub_category_id: "",
      product_id: "",
      start_date: "",
      end_date: "",
    };
    setFilters(resetFilters);
    filtersRef.current = resetFilters;
    loadReportData(resetFilters);
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      // Clean filters - remove empty strings and only send valid filter values
      const cleanedFilters = {};
      if (filters.category_id && filters.category_id !== "") {
        cleanedFilters.category_id = filters.category_id;
      }
      if (filters.sub_category_id && filters.sub_category_id !== "") {
        cleanedFilters.sub_category_id = filters.sub_category_id;
      }
      if (filters.product_id && filters.product_id !== "") {
        cleanedFilters.product_id = filters.product_id;
      }
      if (filters.start_date && filters.start_date !== "") {
        cleanedFilters.start_date = filters.start_date;
      }
      if (filters.end_date && filters.end_date !== "") {
        cleanedFilters.end_date = filters.end_date;
      }

      const blob = await reportService.downloadReportPDF(
        "revenue",
        cleanedFilters
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenue-report-${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const filteredProducts = products.filter((product) => {
    if (filters.category_id) {
      const productCategoryId =
        product.category_id != null ? String(product.category_id) : null;
      if (productCategoryId !== String(filters.category_id)) return false;
    }
    if (filters.sub_category_id) {
      const productSubCategoryId =
        product.sub_category_id != null
          ? String(product.sub_category_id)
          : null;
      if (productSubCategoryId !== String(filters.sub_category_id))
        return false;
    }
    return true;
  });

  const headers = useMemo(
    () => [
      {
        prop: "sr_no",
        title: "Sr. No.",
        isSortable: true,
        cell: (row) => row.sr_no,
      },
      {
        prop: "monthSortValue",
        title: "Month / Year",
        isSortable: true,
        cell: (row) => row.monthLabel,
      },
      {
        prop: "totalPurchaseRevenue",
        title: "Total Purchase Revenue",
        isSortable: true,
        cell: (row) => formatCurrency(row.totalPurchaseRevenue || 0),
      },
      {
        prop: "totalOrderRevenue",
        title: "Total Order Revenue (Gross)",
        isSortable: true,
        cell: (row) => formatCurrency(row.totalOrderRevenue),
      },
      {
        prop: "discountRevenue",
        title: "Discount Revenue",
        isSortable: true,
        cell: (row) => formatCurrency(row.discountRevenue),
      },
      {
        prop: "netSales",
        title: "Net Sales",
        isSortable: true,
        cell: (row) => formatCurrency(row.netSales || 0),
      },
      {
        prop: "netProfit",
        title: "Net Profit",
        isSortable: true,
        cell: (row) => formatCurrency(row.netProfit),
      },
    ],
    []
  );

  const labels = useMemo(
    () => ({ beforeSelect: " " }),
    []
  );

  return (
    <Container fluid className="report-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Reports / Revenue Report
        </Link>
      </div>

      {/* Filters Section */}
      <Card className="filter-card">
        <Card.Header className="filter-header">
          <h5>Filters</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.start_date}
                  onChange={(e) =>
                    handleFilterChange("start_date", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.end_date}
                  onChange={(e) =>
                    handleFilterChange("end_date", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                variant="primary"
                className="report-btn-search"
                onClick={handleSearch}
              >
                <FaSearch /> Search
              </Button>
              <Button
                variant="outline-secondary"
                className="ms-2"
                onClick={handleReset}
              >
                Reset
              </Button>
            </Col>
            {/* <Col md={3} className="d-flex align-items-end justify-content-end">
              <Button
                variant="danger"
                className="report-btn-download"
                onClick={handleDownloadPDF}
                disabled={downloading || loading || reportData.length === 0} // ADDED CONDITION HERE
              >
                {downloading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <FaFilePdf /> Download PDF
                  </>
                )}
              </Button>
            </Col> */}
          </Row>
        </Card.Body>
      </Card>

      {/* Report Content */}
      <Card className="report-content-card">
        <Card.Body>
          {reportData.length === 0 && loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DatatableWrapper
              body={reportData}
              headers={headers}
              paginationOptionsProps={{
                initialState: { rowsPerPage: 10, options: [10, 25, 50, 100] },
              }}
            >
              <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="flex-grow-1 me-2"
                    style={{ maxWidth: "300px" }}
                  >
                    <Filter
                      placeholder="Search by month..."
                      className="w-100"
                    />
                  </div>
                  <PaginationOptions labels={labels} />
                  <InfoPill left="Total Months" right={reportData.length} />
                </div>
                <div>
                  <Button
                    variant="danger"
                    className="report-btn-download"
                    onClick={handleDownloadPDF}
                    disabled={downloading || loading || reportData.length === 0}
                  >
                    {downloading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <FaFilePdf /> Download PDF
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="table-responsive shadow-sm" style={{ position: "relative" }}>
                {loading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      borderRadius: "0.375rem",
                    }}
                  >
                    <Spinner animation="border" variant="primary" />
                  </div>
                )}
                <table className="table table-striped table-bordered align-middle">
                  <TableHeader />
                  <TableBody />
                </table>
              </div>

              {/* Pagination */}
              <div className="compact-pagination d-flex justify-content-left mt-3">
                <Pagination classes={{
                  button: "bg-dark",
                }} />
              </div>
            </DatatableWrapper>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RevenueReport;
